import { NGXLogger } from 'ngx-logger';
import { Component, OnInit, OnDestroy, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, NgForm, FormGroupDirective } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable, Subject, of } from 'rxjs';
import { switchMap, debounceTime, tap, map, distinctUntilChanged } from 'rxjs/operators';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NotificationBannerService } from '@bci-web-core/core';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';

import { MessageService, ToastTypes } from '@rexroth/core';
import { ProductVariantService } from '../_services/product-variant.service';
import { NavigationService } from '../_services/navigation.service';
import { ModuleData } from '../_services/module-data';
import { ParameterService } from '../_services/parameter.service';
import { ModuleDataService } from '../_services/module-data.service';
import { JobService } from '../_services/job.service';
import { DEFINITIONS } from './../shared/app-definitions';
import { isMaterialNumberInput } from '../shared/typecode.utils';
import { JobInfoDTO } from '../_models/job-info-dto';
import { IProductVariant } from '../_models/product-variant';
import { ProductVariant } from '../_models/product-variant';
import { DataPortalIFrameService} from '../_services/data-portal-iframe.service';

type JobServiceResponse = Partial<JobInfoDTO> & {
  isError?: boolean;
  status?: string | number;
  statusText?: string;
  message?: string;
};

type DownloadEntry = { title: string; links: { file: { href: string } } };

@Component({
    selector: 'app-generator',
    templateUrl: './generator.component.html',
    styleUrls: ['./generator.component.scss'],
    standalone: false
})

export class GeneratorComponent implements OnInit, OnDestroy {
  private logger = inject(NGXLogger);
  public messageService = inject(MessageService);
  public notificationBannerService = inject(NotificationBannerService);
  public parService = inject(ParameterService);
  public moduleDataService = inject(ModuleDataService);
  public jobService = inject(JobService);
  private navigationService = inject(NavigationService);
  private productVariantService = inject(ProductVariantService);
  private cd = inject(ChangeDetectorRef);
  public translate = inject(TranslateService);
  private iFrameService = inject(DataPortalIFrameService);


  // access to form input field (typecode or materialnumber)
  @ViewChild('typeIdInputPartVar') public userTypeIdInputPartVar!: NgForm;

  // state texts
  private generationInWorkText!: string;
  private generateSuccessText!: string;
  private generateErrorText!: string;
  private typeCodeErrorInvalidText!: string;
  private downloadErrorText!: string;
  private downloadErrorSingleFileNotFoundText!: string;
  
  public matInfoSnackBarRef: MatSnackBarRef<SimpleSnackBar> | undefined;
  public matResultSnackBarRef: MatSnackBarRef<SimpleSnackBar> | undefined;

  // generator placeholder texts
  public typeCodePlaceholder: string = '';
  private typeCodePlaceholderText!: string;
  private typeCodePlaceholderAutocompleteText!: string;

  // type code variables
  public typeCodeRootPart: string = '';
  public typeCodeInputPart: string = '';
  public typeCodeComplete: string = '';
  public typeCodeForJobServerRequest: string = '';
  public typeCodeIsValid = false;
  public phcodes: string[] = [];

  // job variables
  private compomentIsVisible = false;
  public jobActive = false;
  public jobSuccess = false;
  public jobError = false;
  public jobStatus = '_JobStatus';
  public jobMessage = '';
  public alertClass = 'alert-info';
  public downloads: Array<{ title: string; links: { file: { href: string } } }> = [];
  public showDownloads = false;
  private actJobId: string | undefined;

  // autocomplete modes
  public autocompleteTypeCodeMode = true;
  public materialNumber: string | undefined = undefined;
  public autocompTypeList$: Observable<ProductVariant[]> = of<ProductVariant[]>([]);
  public autocompTypeListCurrent: IProductVariant[] = [];
  public alternativeGenerators: ModuleData[] = [];
  public alternativeMaterialGenerators: Array<{ generator: ModuleData; typecode: string }> = [];
  private isLoadingAlternativeMaterialGenerators = false;
  private alternativeMaterialRequestId = 0;
  private isSwitchingGenerator = false;
  public typeCodeInputPart$: Subject<string> = new Subject<string>();

  // PoC: unified input format states (typecode + material number).
  // 'incomplete' means syntactically valid so far, but not complete yet (warning level).
  public typeCodeFormatState: 'empty' | 'incomplete' | 'incomplete-or-invalid' | 'invalid-format' | 'invalid-format-too-long' | 'valid-format' = 'empty';
  public showTypeCodeIncompleteHint = false;
  public materialNumberFormatState: 'empty' | 'incomplete' | 'invalid-format' | 'invalid-format-too-long' | 'valid-format' = 'empty';
  public showMaterialNumberIncompleteHint = false;

  // Temporary switch: keep PoC format validation logic in code, but disable runtime behavior for now.
  // Later this should be replaced by rule data from ModuleDataService (e.g. length/pattern per moduleId).
  private readonly enableTypeCodeFormatValidation = true;

  moduleData: ModuleData | undefined;

  public currentGeneratorLanguage: string = '';
  public matcher = new MyErrorStateMatcher();
  public downloadInProcess = false;
  public notificationBannerId: string = '';

  constructor() {

    this.logger.info('(generator.component) => constructor()');

    // init language
    if (this.currentGeneratorLanguage !== this.parService.language) {
      this.translate.use(this.parService.language);
      this.currentGeneratorLanguage = this.parService.language;
    }
    // read strings
    this.readTexts();
  }

  async ngOnInit() {
    this.compomentIsVisible = true;
    this.navigationService.initQueryParams();

    // autocomplete
    this.initAutoComplete();

    // validate typeCode (redirect with default values in case of invalid type code)
    if (!DEFINITIONS.regexBosTypeCode.test(this.parService.rootPartNr)) {
      await this.redirectToOverviewPage();
      return;
    }

    // query module data
    this.initComponent();

    // update sidebar menu (order tracker menu item)
    if (this.parService.rootPartNr) {
      this.navigationService.updateRouteParameters({ rootPartNr: this.parService.rootPartNr });
    }

    // try to attach to last running job
    this.jobService.attachClientToJob(this.typeCodeForJobServerRequest,
    (response) => {
      this.handleResponse(response as JobInfoDTO);
    });

    this.handleIFrameDataPortal();
  }

  async handleIFrameDataPortal() {
    this.logger.info('(generator.component) handleIFrameDataPortal() => Await for Promise!');
    await this.iFrameService.handleDataPortalCall();
  }

  ngOnDestroy(): void {
    this.compomentIsVisible = false;
    this.disposeInfoSnackBar();
    this.jobService.detachClient();
    this.typeCodeInputPart$.unsubscribe();
  }

  private readTexts() {
    this.translate.get([
      '_GenerationInWork',
      '_GenerateSuccess',
      '_GenerateError',
      '_InvalidTypeCodeFormat',
      '_TypeCodePlaceholder',
      '_TypeCodePlaceholderAutocomplete',
      '_DownloadError',
      '_DownloadErrorSingleFileNotFound'])
      .subscribe(
        data => {
          this.generationInWorkText = data._GenerationInWork;
          this.generateSuccessText = data._GenerateSuccess;
          this.generateErrorText = data._GenerateError;
          this.typeCodeErrorInvalidText = data._InvalidTypeCodeFormat;
          this.typeCodePlaceholderText = data._TypeCodePlaceholder;
          this.typeCodePlaceholderAutocompleteText = data._TypeCodePlaceholderAutocomplete;
          this.downloadErrorText = data._DownloadError;
          this.downloadErrorSingleFileNotFoundText = data._DownloadErrorSingleFileNotFound;
        });
  }


private initComponent(): void {
  if (!this.moduleData) {
    const rawRootPartNr = this.parService.calledTypeCode;

    // rawRootPartNr is material number (try to get typecode)
    if (isMaterialNumberInput(rawRootPartNr)) {
      this.productVariantService.getVariants(this.moduleData, rawRootPartNr).subscribe(
        (variants: IProductVariant[]) => {
          if (variants && variants.length === 1) {
            // take first one
            const typecode = variants[0].typecode;
            // get module data from server
            this.moduleDataService.getModuleDataBestMatch(typecode).subscribe((data) => {
              this.moduleData = data;
              if (!this.moduleData.moduleId) {
                this.moduleData = undefined;
                this.redirectToOverviewPage();
                return;
              }
              this.initModuleData();
              this.initJobVariables();
            });
          } else {
            // no variant found
            this.redirectToOverviewPage();
          }
        },
        (_error) => {
          void _error;
          // error backend call
          this.redirectToOverviewPage();
        }
      );
      return; 
    }

    // get module data from server
    this.moduleDataService.getModuleDataBestMatch(this.parService.calledTypeCode).subscribe((data) => {
      this.moduleData = data;
      if (!this.moduleData.moduleId) {
        this.moduleData = undefined;
        this.redirectToOverviewPage();
        return;
      }
      this.initModuleData();
      this.initJobVariables();
    });
  } else {
    this.initModuleData();
    this.initJobVariables();
  }
}

  private initModuleData(): void {
    this.typeCodeRootPart = this.moduleData!.moduleId;
    this.phcodes = this.moduleData!.phcodes;  
    const rawRootPartNr = this.parService.calledTypeCode;
    if (isMaterialNumberInput(rawRootPartNr)) {
      this.typeCodeInputPart = rawRootPartNr;
    } else {
      this.typeCodeInputPart = rawRootPartNr.substring(this.typeCodeRootPart.length);
    }

    this.checkInput();
    this.setMaterialNumberByTypecode(this.typeCodeComplete);
  }

  private initJobVariables(): void {
    this.typeCodeIsValid = true;
    this.jobActive = false;
    this.jobSuccess = false;
    this.jobError = false;
    this.jobStatus = '_JobStatus';
    this.jobMessage = '';
    this.alertClass = 'alert-info';
    this.downloads = [
      // // Test data
      // {
      //   title: 'COREX-C-X3-11-ANNN-21.01-01RS-NN-NN.edz', 
      //   links: { file: { href: 'https://ecad-configuration-dev.apps.de1.bosch-iot-cloud.com/usw.edz' } }
      // },
      // {
      //   title: 'COREX-C-X3-11-ANNN-21.01-01RS-NN-NN.pdf',
      //   links: { file: { href: 'https://ecad-configuration-dev.apps.de1.bosch-iot-cloud.com/usw.pdf' } }
      // }
    ];
    this.showDownloads = this.parService.downloads;
  }

  private async redirectToOverviewPage() {
    await this.navigationService.navigate('/overview');
  }

  public openLink(controlId: string, newTab: boolean): void {
    const cntrl = document.getElementById(controlId);
    if (cntrl !== null) {
      const href = cntrl.getAttribute('href');
      if (newTab) {
        if (href) {window.open(href, '_blank');}           // new tab
      } else {
        if (href) {window.open(href, '_self');}            // current tab
      }
    }
  }

  private checkForOtherValidGenerators() {
    // Check whether the current input can be handled by another generator.
    if (this.typeCodeInputPart.length > 0) {
      this.moduleDataService.getGeneratorSwitchMatches(this.typeCodeInputPart.toUpperCase()).subscribe((matches) => {
        const nextAlternatives = matches.filter((data) => data.moduleId && data.moduleId !== this.moduleData?.moduleId);
        this.alternativeGenerators = nextAlternatives;
        if (this.alternativeGenerators.length > 0) {
          this.logger.info('(generator.component) Alternative Generators found: ' + this.alternativeGenerators.map((item) => item.moduleId).join(', '));
        } else {
          this.logger.info('(generator.component) No alternative Generator found.');
        }
      });
    } else {
      this.alternativeGenerators = [];
    }
  }

  // Central input handler called on every keystroke, paste, and programmatic value change.
  // Normalizes input, detects typecode-vs-material-number mode, updates format states,
  // triggers form validation, and pushes the new value into the autocomplete stream.
  // preserveMaterialNumber=true skips clearing materialNumber (used after explicit autocomplete selection).
  public checkInput(preserveMaterialNumber = false): void {
    this.cd.detectChanges();   // Important: overtake initial value (e.g. from paste)

    if (typeof this.typeCodeInputPart === 'string') {
      //replace none breaking spaces by normal spaces
      this.typeCodeInputPart = this.replaceNoneBreakingSpace(this.typeCodeInputPart);

      this.typeCodeInputPart = this.typeCodeInputPart.toUpperCase().replace(new RegExp(`^${this.typeCodeRootPart}`), '');  //remove root part from begin of input part
      this.typeCodeComplete = this.typeCodeRootPart + this.typeCodeInputPart;
      this.typeCodeForJobServerRequest = this.typeCodeComplete.replace(/\//g, '%2F');

      //check for alternative generator
      this.checkForOtherValidGenerators();
    }  

    // Keep mode in sync immediately on keystroke (not only via debounced autocomplete stream).
    this.autocompleteTypeCodeMode = !isMaterialNumberInput(this.typeCodeInputPart);

    if (!preserveMaterialNumber) {
      // During regular typing, hide stale material display until explicit selection/generate flow sets it.
      this.materialNumber = '';
    }

    if (!this.autocompleteTypeCodeMode) {
      this.materialNumberFormatState = this.getMaterialNumberFormatState();
    } else {
      this.materialNumberFormatState = 'empty';
      this.showMaterialNumberIncompleteHint = false;
    }
    this.updateMaterialNumberValidationErrors();

    if (!this.autocompleteTypeCodeMode || !this.enableTypeCodeFormatValidation) {
      // Keep validation hooks in place but disable typecode format enforcement when feature flag is off.
      this.typeCodeFormatState = 'empty';
      this.showTypeCodeIncompleteHint = false;
      this.updateTypeCodeValidationErrors();

      if (this.typeCodeInputPart$.observers) {
        this.typeCodeInputPart$.next(this.typeCodeInputPart);
      }
      return;
    }

    // Update typecode format validation state
    this.typeCodeFormatState = this.getTypeCodeFormatState();
    this.updateTypeCodeValidationErrors();

    // handle autocomplete
    if (this.typeCodeInputPart$.observers) {
      this.typeCodeInputPart$.next(this.typeCodeInputPart);
    }
  }

  private getMaterialNumberFormatState(): 'empty' | 'incomplete' | 'invalid-format' | 'invalid-format-too-long' | 'valid-format' {
    const input = (this.typeCodeInputPart || '').trim().toUpperCase();
    return this.getMaterialNumberFormatStateByInput(input);
  }

  private getMaterialNumberFormatStateByInput(inputValue: string): 'empty' | 'incomplete' | 'invalid-format' | 'invalid-format-too-long' | 'valid-format' {
    const input = (inputValue || '').trim().toUpperCase();
    if (!isMaterialNumberInput(input)) {
      return 'empty';
    }

    if (input.length > DEFINITIONS.materialNumberLength) {
      return 'invalid-format-too-long';
    }

    const suffix = input.substring(2);
    if (suffix.length > 0 && /[^0-9]/.test(suffix)) {
      return 'invalid-format';
    }

    if (input.length < DEFINITIONS.materialNumberLength) {
      return 'incomplete';
    }

    return 'valid-format';
  }

  // Set form control errors based on material number validation state.
  // Preserves unrelated error keys (e.g. typeCodeMatNumberValidator) by merging into existing errors.
  private updateMaterialNumberValidationErrors(): void {
    if (!this.userTypeIdInputPartVar?.control) { return; }
    const control = this.userTypeIdInputPartVar.control;
    const errors = control.errors || {};
    const nextErrors: Record<string, unknown> = { ...errors };

    delete nextErrors.materialNumberInvalidFormat;
    delete nextErrors.materialNumberTooLong;
    delete nextErrors.materialNumberIncomplete;

    if (this.materialNumberFormatState === 'invalid-format') {
      nextErrors.materialNumberInvalidFormat = true;
    } else if (this.materialNumberFormatState === 'invalid-format-too-long') {
      nextErrors.materialNumberTooLong = true;
    } else if (this.materialNumberFormatState === 'incomplete' && this.showMaterialNumberIncompleteHint) {
      nextErrors.materialNumberIncomplete = true;
    }

    control.setErrors(Object.keys(nextErrors).length > 0 ? nextErrors : null);
    this.cd.markForCheck();
  }

  // Set form control errors based on typecode validation state.
  private updateTypeCodeValidationErrors(): void {
    if (!this.userTypeIdInputPartVar) { return; }
    const control = this.userTypeIdInputPartVar.control;
    if (!control) { return; }

    const errors = control.errors || {};
    const nextErrors: Record<string, unknown> = { ...errors };
    delete nextErrors.typeCodeInvalidFormat;
    delete nextErrors.typeCodeTooLong;
    delete nextErrors.typeCodeIncompleteFormat;
    delete nextErrors.typeCodeIncompleteOrInvalidFormat;

    if (this.typeCodeFormatState === 'invalid-format') {
      nextErrors.typeCodeInvalidFormat = true;
    } else if (this.typeCodeFormatState === 'invalid-format-too-long') {
      nextErrors.typeCodeTooLong = true;
    } else if (this.typeCodeFormatState === 'incomplete' && this.showTypeCodeIncompleteHint) {
      // Warning only shown on blur
      nextErrors.typeCodeIncompleteFormat = true;
    } else if (this.typeCodeFormatState === 'incomplete-or-invalid' && this.showTypeCodeIncompleteHint) {
      // Warning only shown on blur
      nextErrors.typeCodeIncompleteOrInvalidFormat = true;
    }

    control.setErrors(Object.keys(nextErrors).length > 0 ? nextErrors : null);
    this.cd.markForCheck();
  }

  // Typecode format validation driven by parts.config rules.
  // Rules are matched by productid prefix and can define one or multiple allowed lengths.
  private getTypeCodeFormatState(): 'empty' | 'incomplete' | 'incomplete-or-invalid' | 'invalid-format' | 'invalid-format-too-long' | 'valid-format' {
    const input = this.typeCodeInputPart;
    if (!input || input.length === 0) {
      return 'empty';
    }
    if (isMaterialNumberInput(input)) {
      return 'empty';
    }
    if (input.includes('_')) {
      return 'invalid-format';
    }

    const allowedLengths = this.getAllowedTypeCodeInputLengths();
    if (allowedLengths.length === 0) {
      if (this.hasMatchingTypeCodeRule()) {
        // Matching product rule exists, but no explicit length constraint for this rule.
        return 'valid-format';
      }

      // At least one product rule has explicit length constraints in this module,
      // but current input does not match any product rule prefix.
      if (this.hasConfiguredTypeCodeLengthRules()) {
        const configuredMaxLength = this.getConfiguredTypeCodeMaxLength();
        const completeTypeCodeLength = `${(this.typeCodeRootPart || '').trim().toUpperCase()}${input}`.length;
        if (configuredMaxLength > 0 && completeTypeCodeLength > configuredMaxLength) {
          return 'invalid-format-too-long';
        }
        return 'incomplete-or-invalid';
      }

      // No explicit typecode length rules configured for this module.
      return 'valid-format';
    }

    // Length rules refer to the full typecode (generator prefix + user input).
    const completeTypeCodeLength = `${(this.typeCodeRootPart || '').trim().toUpperCase()}${input}`.length;

    const minLength = allowedLengths[0];
    const maxLength = allowedLengths[allowedLengths.length - 1];

    if (completeTypeCodeLength > maxLength) {
      return 'invalid-format-too-long';
    }

    if (allowedLengths.includes(completeTypeCodeLength)) {
      return 'valid-format';
    }

    if (completeTypeCodeLength < minLength) {
      return 'incomplete';
    }

    // Between min and max but not exactly allowed (e.g. [20,30] and input=25).
    return 'incomplete-or-invalid';
  }

  private hasConfiguredTypeCodeLengthRules(): boolean {
    return (this.moduleData?.productIdRules || []).some((rule) =>
      Array.isArray(rule.lengths)
      && rule.lengths.some((length) => Number.isInteger(length) && length > 0)
    );
  }

  private getConfiguredTypeCodeMaxLength(): number {
    const configuredLengths = (this.moduleData?.productIdRules || [])
      .flatMap((rule) => Array.isArray(rule.lengths) ? rule.lengths : [])
      .filter((length) => Number.isInteger(length) && length > 0);

    if (configuredLengths.length === 0) {
      return 0;
    }

    return Math.max(...configuredLengths);
  }

  private hasMatchingTypeCodeRule(): boolean {
    const inputPart = (this.typeCodeInputPart || '').trim().toUpperCase();
    const rootPart = (this.typeCodeRootPart || '').trim().toUpperCase();
    if (!inputPart || !rootPart || !this.moduleData?.productIdRules?.length) {
      return false;
    }

    const completeTypeCode = `${rootPart}${inputPart}`;
    return this.moduleData.productIdRules.some((rule) => {
      const ruleId = (rule.id || '').toUpperCase();
      return ruleId.length > 0
        && (completeTypeCode.startsWith(ruleId) || ruleId.startsWith(completeTypeCode));
    });
  }

  private getAllowedTypeCodeInputLengths(): number[] {
    const inputPart = (this.typeCodeInputPart || '').trim().toUpperCase();
    const rootPart = (this.typeCodeRootPart || '').trim().toUpperCase();
    if (!inputPart || !rootPart || !this.moduleData?.productIdRules?.length) {
      return [];
    }

    const completeTypeCode = `${rootPart}${inputPart}`;
    const matchingRules = this.moduleData.productIdRules.filter((rule) => {
      const ruleId = (rule.id || '').toUpperCase();
      return ruleId.length > 0
        && (completeTypeCode.startsWith(ruleId) || ruleId.startsWith(completeTypeCode));
    });

    if (matchingRules.length === 0) {
      return [];
    }

    const mergedLengths = matchingRules.flatMap((rule) => Array.isArray(rule.lengths) ? rule.lengths : []);
    return Array.from(new Set(mergedLengths.filter((length) => Number.isInteger(length) && length > 0))).sort((a, b) => a - b);
  }

  private replaceNoneBreakingSpace(str: string): string {
    const re = new RegExp(String.fromCharCode(160), "g");
    return str.replace(re, " ");
  }

  public onTypeCodeInputPartClear() {
    this.typeCodeInputPart = '';
    this.materialNumber = '';
    this.checkInput();
  }

  public onTypeCodeInputFocus(): void {
    this.showTypeCodeIncompleteHint = false;
    this.showMaterialNumberIncompleteHint = false;
    this.updateMaterialNumberValidationErrors();
    this.updateTypeCodeValidationErrors();
  }

  public onTypeCodeInputBlur(): void {
    this.showTypeCodeIncompleteHint = this.enableTypeCodeFormatValidation
      && (this.typeCodeFormatState === 'incomplete' || this.typeCodeFormatState === 'incomplete-or-invalid');
    this.showMaterialNumberIncompleteHint = !this.autocompleteTypeCodeMode && this.materialNumberFormatState === 'incomplete';
    this.updateMaterialNumberValidationErrors();
    this.updateTypeCodeValidationErrors();
  }

  public onTypeCodeInputEnter(event?: KeyboardEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const regularCount = this.autocompTypeListCurrent.length;
    const switchCount = this.alternativeGenerators.length + this.alternativeMaterialGenerators.length;
    const totalSelectableEntries = regularCount + switchCount;

    // Apply Enter only when exactly one visible autocomplete entry remains.
    if (totalSelectableEntries !== 1) {
      return;
    }

    if (regularCount === 1) {
      this.onOptionSelected(this.autocompTypeListCurrent[0] as ProductVariant);
      return;
    }

    if (this.alternativeGenerators.length === 1) {
      this.onAlternativeGeneratorSelected(this.alternativeGenerators[0]);
      return;
    }

    if (this.alternativeMaterialGenerators.length === 1) {
      this.onAlternativeMaterialGeneratorSelected(this.alternativeMaterialGenerators[0]);
    }
  }

  public onSendTypeCode(): void {
    //keyboard Return => check for cancellation
    if( this.isBtnGenerateDisabled()) {
      return;
    }

    // synchronize device data with autocomplete data
    this.syncDeviceDataWithAutocompleteData();

    // required: set values in case of autocomplete selection
    this.typeCodeInputPart = this.getTypeCodeInputPart(this.typeCodeInputPart);
    this.typeCodeComplete = this.typeCodeRootPart + this.typeCodeInputPart;
    this.typeCodeForJobServerRequest = this.typeCodeComplete.replace(/\//g, '%2F');

    if (this.jobActive) {
      return;
    }
    this.initJobVariables();

    // reload page to put typeCode in query params
    this.navigationService.navigate('/generator', {
      moduleId: this.typeCodeComplete,
      replaceInHistory: true,
      reloadRoute: false });

    // save session params
    sessionStorage.setItem('ecad.generator.TypeCode', this.typeCodeComplete);
    sessionStorage.setItem('ecad.generator.MaterialNumber', this.materialNumber ?? '');

    // update sidebar menu with new generator typeCode
    this.navigationService.updateRouteParameters({ rootPartNr: 'BOS.' + this.typeCodeComplete + '**' });
    if (this.typeCodeIsValid) {
      this.resultNotificationEnabled = true;
      this.jobService.sendJob(this.typeCodeForJobServerRequest, this.materialNumber || '',
        (response) => {
          const jobResponse = response as JobServiceResponse;
          if (jobResponse.isError === true) {
            this.handleResponse(jobResponse as JobInfoDTO);
          } else {
            const jobInfoDTO = new JobInfoDTO(jobResponse);
            this.handleResponse(jobInfoDTO);
          }
        });
    }
  }

  private handleError(response: unknown): void {
    const errResponse = response as { status?: number; statusText?: string };
    const status = errResponse.status ?? 0;
    const statusText = errResponse.statusText ?? '';

    this.jobError = true;
    this.jobActive = false;
    this.jobStatus = status + ': ' + statusText;
    this.alertClass = 'alert-danger';
    this.showErrorNotification(status, this.jobStatus);
  }

  private getDownloadLink(jobInfoDTO: JobInfoDTO): void {
    if (jobInfoDTO.hrefEdzLink.length > 4) {
      // read links from cache entry
      this.downloads = [
        { title: jobInfoDTO.titleEdzLink, links: { file: { href: jobInfoDTO.hrefEdzLink } } },
        { title: jobInfoDTO.titlePdfLink, links: { file: { href: jobInfoDTO.hrefPdfLink } } },
      ];
    } else {
      // query links from JobServer
      this.jobService.getDownloadLink(this.actJobId || '', (response) => {
        const downloadResponse = response as JobServiceResponse | DownloadEntry[];
        if (!Array.isArray(downloadResponse) && downloadResponse.isError === true) {
          this.handleError(downloadResponse);
        } else {
          this.downloads = downloadResponse as DownloadEntry[];
        }
      });
    }
  }

  private handleResponse(jobInfoDTO: JobInfoDTO): void {
    if (jobInfoDTO.isError === true) {
      // in case if http level errors
      this.handleError(jobInfoDTO);
    } else {
      this.jobActive = true;
      this.actJobId = jobInfoDTO.jobId;
      this.jobStatus = jobInfoDTO.status;

      if (!this.jobStatus.startsWith('Finished')) {
        this.showInfoNotification(this.jobStatus);
      }

      if (this.jobStatus === DEFINITIONS.jobState_Successful) {
        this.alertClass = 'alert-success';
        this.jobSuccess = true;
        this.getDownloadLink(jobInfoDTO);
        this.jobActive = false;
        this.jobStatus = '_GenerateSuccess';
        this.showSuccessNotification();
      } else if (this.jobStatus.startsWith(DEFINITIONS.jobState_Error)) {
        this.alertClass = 'alert-danger';
        this.jobSuccess = false;
        this.jobActive = false;
        this.jobError = true;
        this.jobStatus = (jobInfoDTO.status === DEFINITIONS.jobState_Error_TypeCode) ? '_TypeCodeInvalid' : '_GenerateError';

        // notification only if component is visible
        this.showErrorNotification(0, 'FinishedInError');
      }
    }
  }

  public getTypeCodeInputPart(fullTypeCode: string): string {
    if (this.typeCodeRootPart) {
      return (fullTypeCode.toUpperCase().replace(new RegExp(`^${this.typeCodeRootPart}`), '')).trim();  //remove root part from begin of input part
    }
    return fullTypeCode.trim();
  }

  public async onDownloadSingleFile(hrefFile: string) {
    // Check if download already runs
    if (this.downloadInProcess) {
      return;
    }
    this.disposeResultSnackBar();
    this.downloadInProcess = true;
    try {
      // download and save single file
      const response = await this.jobService.downloadSingleFile(hrefFile);
      if (response) {
        saveAs(response, hrefFile.split('/').pop());
      }
    } catch (err) {
      const errResponse = err as { status?: number; statusText?: string; message?: string };
      this.logger.error('(generator.component) ERROR jobService.downloadSingleFile(..), status=' + errResponse.status + ', statusText=' + errResponse.statusText);
      if (errResponse.status === 404) {
        this.showDownloadErrorNotification(this.downloadErrorSingleFileNotFoundText);
      } else {
        this.showDownloadErrorNotification(errResponse.message ?? 'Unknown error');
      }
    }
    // unlock download
    this.downloadInProcess = false;
  }

  private showInfoNotification(jobStatus: string): void {
    const message = this.generationInWorkText + ' (' + jobStatus + ')';
    if (this.compomentIsVisible) {
      // show or update notification
      if (!this.matInfoSnackBarRef || this.matInfoSnackBarRef.containerInstance.snackBarConfig.data.message.indexOf(jobStatus) === -1) {
        this.matInfoSnackBarRef = this.messageService.toast( ToastTypes.Information, message);
        this.matInfoSnackBarRef.containerInstance.snackBarConfig.duration = 6000000;   //set toast duration to 100min
        this.resultNotificationEnabled = true;
      }
    }
  }

  private showSuccessNotification(): void {
    const message = this.generateSuccessText;
    if (this.compomentIsVisible && this.resultNotificationEnabled) {
      this.messageService.toast( ToastTypes.Success, message);
      this.resultNotificationEnabled = false;
      this.disposeInfoSnackBar();
    }
  }

  private showErrorNotification(httpRespStatus: number, jobStatus: string): void {
    let message = (httpRespStatus === 400) ? this.typeCodeErrorInvalidText : this.generateErrorText;
    if (this.compomentIsVisible && this.resultNotificationEnabled) {
      if (jobStatus !== 'FinishedInError' && httpRespStatus !== 400) {
        message = message + ' (' + jobStatus + ').';
      }
      this.messageService.toast( ToastTypes.Error, message);
      this.resultNotificationEnabled = false;
      this.disposeInfoSnackBar();
    }
  }

  private showDownloadErrorNotification(errorText: string): void {
    const message = this.downloadErrorText  + ' (' + errorText + ')';
    if (this.compomentIsVisible) {
      this.matResultSnackBarRef = this.messageService.toast( ToastTypes.Error, message);
      this.disposeInfoSnackBar();
    }
  }

  private disposeInfoSnackBar() {
    if (this.matInfoSnackBarRef) {
      this.matInfoSnackBarRef.dismiss();
      this.matInfoSnackBarRef = undefined;
    }
  }

  private disposeResultSnackBar() {
    if (this.matResultSnackBarRef) {
      this.matResultSnackBarRef.dismiss();
      this.matResultSnackBarRef = undefined;
    }
  }

  private getGenModeQueryString() {
    // get generator running mode
    let genmode = sessionStorage.getItem(DEFINITIONS.sessParGenMode);
    const eplanversion = sessionStorage.getItem(DEFINITIONS.sessParEplanVersion);
    genmode = (eplanversion) ? (genmode + ',' + eplanversion) : genmode;

    // encode ? to enable EPLAN query param handling 
    return ("%3Fgenmode=" + genmode);
  }

    public getImportLink(): string | undefined {
    if (this.downloads[0]) {
      return 'https://eplandata.de/portal/portal.php?action=api-importPartFromUrl&url=' + this.downloads[0].links.file.href + this.getGenModeQueryString();
    } else {
        return undefined;
    }
  }

    public getMakroLink(): string | undefined {
    if (this.downloads[0]) {
      const title = this.downloads[0].title;
      const partNr = isMaterialNumberInput(title) ? title.substring(0, title.lastIndexOf('.')) : this.typeCodeComplete;
      return 'https://eplandata.de/portal/portal.php?action=api-placeDeviceFromUrl&url=' + this.downloads[0].links.file.href + this.getGenModeQueryString() +
             '&partnr=BOS.' + partNr;
    } else {
        return undefined;
    }
  }

  public sendSupportRequest(): void {
    window.open('mailto:ECAD-Configuration.Support@boschrexroth.de?subject=' +
                '[ECAD-Configuration] Support Request (Typecode: ' + this.typeCodeComplete + ')', '_blank');
  }

  public get resultNotificationEnabled(): boolean {
    if (sessionStorage.getItem('ecad.Generator.resultNotificationEnabled') === 'true') {
      return true;
    } else {
      return false;
    }
  }
  public set resultNotificationEnabled(value: boolean) {
    sessionStorage.setItem('ecad.Generator.resultNotificationEnabled', value.toString());
  }

  // Multi-stage gate that controls Generate button availability.
  // Checked on every state change; returns true (disabled) at the first failing condition.
  public isBtnGenerateDisabled(): boolean {
    //1. Disable if typeCode prefix, input field, or job is active
    if (!this.typeCodeRootPart || !this.typeCodeInputPart || this.jobActive === true) {
      return true;
    }

    //2. Disable if input field validation error
    if (this.userTypeIdInputPartVar?.hasError('typeCodeMatNumberValidator')) {
      return true;
    }

    // 2.25. Material mode: enable only for a complete 10-digit material number with one exact regular hit.
    if (!this.autocompleteTypeCodeMode) {
      return !this.isSingleExactMaterialNumberMatch();
    }

    //2.5. PoC: optional format gate (disabled until backend-driven rules are integrated)
    if (this.enableTypeCodeFormatValidation && this.autocompleteTypeCodeMode && this.typeCodeFormatState !== 'valid-format') {
      return true;
    }


    //3. Disable if autocomplete list is not loaded
    if (!Array.isArray(this.autocompTypeListCurrent)) {
      return true;
    }

    //4. Material number mode: input does not match first autocomplete entry
    if (
      !this.autocompleteTypeCodeMode &&
      this.autocompTypeListCurrent.length > 0 &&
      this.autocompTypeListCurrent[0].materialnumber !== this.typeCodeInputPart
    ) {
      return true;
    }

    //Button can be enabled
    return false;
  }

  private isSingleExactMaterialNumberMatch(): boolean {
    return this.materialNumberFormatState === 'valid-format'
      && this.autocompTypeListCurrent.length === 1;
  }

  // *****************************************************************************************
  //
  // AUTOCOMPLETE SECTION
  //
  // *****************************************************************************************
  initAutoComplete() {
    
    this.typeCodeInputPart$
    .pipe(
      map(value => (value || '').trim().toUpperCase()),
      debounceTime(200),
      distinctUntilChanged(),
      tap((value: string) => {
        this.autocompleteTypeCodeMode = !isMaterialNumberInput(value);
      }),
      switchMap(value => {
        const normalizedValue = (value || '').trim().toUpperCase();
        
        // This branch only short-circuits hard material-number format errors.
        // Typecode input (and non-hard material states) continues through getVariants().
        const materialNumberState = this.getMaterialNumberFormatStateByInput(normalizedValue);
        const hasMaterialNumberError = materialNumberState === 'invalid-format' || materialNumberState === 'invalid-format-too-long';

        // Do not query backend for invalid material numbers.
        if (hasMaterialNumberError) {
          return of<IProductVariant[]>([]);
        }

        return this.productVariantService.getVariants(this.moduleData, value);
      })
    )
    .subscribe({
      next:(productVariants: IProductVariant[]) => {
        this.setPlaceholder(productVariants);
        this.autocompTypeListCurrent = productVariants;
        this.autocompTypeList$ = of<ProductVariant[]>(productVariants);

        // If material number input returns no context-bound results, try context-free lookup
        // for syntactically valid/incomplete input. Only hard errors block requests.
        const hasMaterialNumberError = this.materialNumberFormatState === 'invalid-format'
          || this.materialNumberFormatState === 'invalid-format-too-long';
        if (productVariants.length === 0 && !this.autocompleteTypeCodeMode && !hasMaterialNumberError) {
          this.loadAlternativeMaterialGenerators(this.typeCodeInputPart);
        } else {
          this.alternativeMaterialRequestId++;
          this.isLoadingAlternativeMaterialGenerators = false;
          this.alternativeMaterialGenerators = [];
        }

        //check if input field is already initialized
        if (!this.userTypeIdInputPartVar?.control) { return; }

        const control = this.userTypeIdInputPartVar.control;
        const currentErrors = control.errors || {};

        //do field validation
        if(!this.isTypeCodeInputValid()) {
          control.setErrors({ ...currentErrors, typeCodeMatNumberValidator: { valid: false }});
          this.logger.trace('(generator.component) ## SET validation Error, new value => ' + JSON.stringify(this.userTypeIdInputPartVar.errors));
        } else {
          const cleanedErrors = { ...currentErrors };
          delete cleanedErrors.typeCodeMatNumberValidator;
          control.setErrors(Object.keys(cleanedErrors).length > 0 ? cleanedErrors : null);
          this.logger.trace('(generator.component) ## CLEAR validation Error, new value => ' + JSON.stringify(this.userTypeIdInputPartVar.errors));
        }
      },
      error: (error) => {
        this.alternativeMaterialRequestId++;
        this.isLoadingAlternativeMaterialGenerators = false;
        this.logger.error('(generator.component) Error initAutoComplete() => ' + JSON.stringify(error));
        this.setPlaceholder(undefined);
      }
    });
  }

  // Determines whether the current input passes the "no-data" validator.
  // In material number mode: suppresses the error while the async alternative-generator lookup
  // is still in flight (prevents flicker on Back/Forward), and while switch options are visible.
  private isTypeCodeInputValid(): boolean {
    // Suppress error for non-material-number modes and hard format errors (invalid/too-long).
    // For 'empty' and 'incomplete' states: the backend uses prefix search, so an empty autocomplete
    // list already guarantees no match — the error may be shown even before the number is complete.
    if (!this.autocompleteTypeCodeMode &&
        (this.materialNumberFormatState === 'invalid-format' ||
         this.materialNumberFormatState === 'invalid-format-too-long')) {
      return true;
    }
    // Also suppress for empty input — user has not started typing a material number yet.
    if (!this.autocompleteTypeCodeMode && this.materialNumberFormatState === 'empty') {
      return true;
    }

    // Suppress transient no-data warning while context-free alternative lookup is still in flight.
    // This prevents flickering during Back/Forward navigation (applies to incomplete and valid-format).
    if (!this.autocompleteTypeCodeMode &&
        this.autocompTypeListCurrent.length === 0 &&
        this.isLoadingAlternativeMaterialGenerators) {
      return true;
    }

    // In material number mode, do not show "no data" warning while switch options are available.
    if (!this.autocompleteTypeCodeMode &&
        this.autocompTypeListCurrent.length === 0 &&
        (this.alternativeGenerators.length > 0 || this.alternativeMaterialGenerators.length > 0)) {
      return true;
    }

    //material number has empty autocomplete list
    if(!this.autocompleteTypeCodeMode &&  this.autocompTypeListCurrent.length === 0) {
      return false;
    } 
    //material number doesn't fit to first autocomplete list item (only check when format is complete).
    // For incomplete input, avoid false positives when autocomplete returns 10-char variants.
    if(!this.autocompleteTypeCodeMode &&  
        this.materialNumberFormatState === 'valid-format' &&
        this.autocompTypeListCurrent.length >= 1 &&  
        this.autocompTypeListCurrent[0].materialnumber !== this.typeCodeInputPart) {
      return false;
    }
    return true;
  }

  private setPlaceholder(variantsList: ProductVariant[] | undefined) {
    if (variantsList && variantsList.length > 0) {
      this.typeCodePlaceholder = this.typeCodePlaceholderAutocompleteText;
    } else {
      this.typeCodePlaceholder = this.typeCodePlaceholderText;
    }
  }

  public onOptionSelected(option: ProductVariant) {
    // typecode an materialnumber settings
    this.typeCodeInputPart = this.getTypeCodeInputPart(option.typecode);
    if( !this.materialNumber || (this.typeCodeComplete !== option.typecode.toUpperCase())) {
      this.typeCodeComplete = option.typecode.toUpperCase();
      this.typeCodeForJobServerRequest = this.typeCodeComplete.replace(/\//g, '%2F');
      this.materialNumber = option.materialnumber;
    }

    // Reuse normal input validation flow and preserve explicit material mapping from selection.
    this.checkInput(true);

    // handle autocomplete
    if (this.typeCodeInputPart$.observers) {
      this.typeCodeInputPart$.next(this.typeCodeInputPart);
    }
  }

  // Context-free fallback lookup: queries variants without module context to find generators
  // from other product families that match the given material number.
  // Uses a requestId to discard stale responses when the user types faster than responses arrive.
  // Re-evaluates the "no data" validator once results are available.
  private loadAlternativeMaterialGenerators(matnumber: string): void {
    const requestId = ++this.alternativeMaterialRequestId;
    this.isLoadingAlternativeMaterialGenerators = true;

    this.productVariantService.getVariants(undefined, matnumber).subscribe((variants) => {
      if (requestId !== this.alternativeMaterialRequestId) {
        return;
      }

      const results: Array<{ generator: ModuleData; typecode: string }> = [];
      for (const variant of variants) {
        const matches = this.moduleDataService.getGeneratorSwitchModulesOfInput(variant.typecode);
        for (const match of matches) {
          if (match.moduleId && match.moduleId !== this.moduleData?.moduleId
              && !results.find(r => r.generator.moduleId === match.moduleId)) {
            results.push({ generator: match, typecode: variant.typecode });
          }
        }
      }
      this.alternativeMaterialGenerators = results;
      this.isLoadingAlternativeMaterialGenerators = false;

      // Re-evaluate the "no data" validator after async switch entries arrive.
      if (!this.userTypeIdInputPartVar?.control) { return; }
      const control = this.userTypeIdInputPartVar.control;
      const currentErrors = control.errors || {};
      if (!this.isTypeCodeInputValid()) {
        control.setErrors({ ...currentErrors, typeCodeMatNumberValidator: { valid: false } });
      } else {
        const cleanedErrors = { ...currentErrors };
        delete cleanedErrors.typeCodeMatNumberValidator;
        control.setErrors(Object.keys(cleanedErrors).length > 0 ? cleanedErrors : null);
      }
      this.cd.markForCheck();
    }, () => {
      if (requestId !== this.alternativeMaterialRequestId) {
        return;
      }

      this.isLoadingAlternativeMaterialGenerators = false;
      this.alternativeMaterialGenerators = [];

      if (!this.userTypeIdInputPartVar?.control) { return; }
      const control = this.userTypeIdInputPartVar.control;
      const currentErrors = control.errors || {};
      if (!this.isTypeCodeInputValid()) {
        control.setErrors({ ...currentErrors, typeCodeMatNumberValidator: { valid: false } });
      } else {
        const cleanedErrors = { ...currentErrors };
        delete cleanedErrors.typeCodeMatNumberValidator;
        control.setErrors(Object.keys(cleanedErrors).length > 0 ? cleanedErrors : null);
      }
      this.cd.markForCheck();
    });
  }

  private async persistCurrentGeneratorInputInHistory(): Promise<void> {
    const normalizedInput = (this.typeCodeInputPart || '').trim().toUpperCase();
    if (!this.typeCodeRootPart || !normalizedInput) {
      return;
    }

    const currentInputPart = this.getTypeCodeInputPart(normalizedInput);
    if (!currentInputPart) {
      return;
    }

    const currentModuleId = `${this.typeCodeRootPart}${currentInputPart}`;
    await this.navigationService.navigate('/generator', {
      moduleId: currentModuleId,
      replaceInHistory: true,
      reloadRoute: false
    });
  }

  public async onAlternativeGeneratorSelected(generator: ModuleData, selectionEvent?: { isUserInput?: boolean; source?: { selected?: boolean } }) {
    this.logger.info('(generator.component) Alternative Generator selected: ' + generator.moduleId);
    if (selectionEvent && (!selectionEvent.isUserInput || !selectionEvent.source?.selected)) { return; }
    if (this.isSwitchingGenerator) { return; }

    const normalizedInput = (this.typeCodeInputPart || '').trim().toUpperCase();
    const normalizedModuleId = (generator.moduleId || '').trim().toUpperCase();
    const targetModuleId = normalizedInput.length < normalizedModuleId.length ? normalizedModuleId : normalizedInput;

    this.isSwitchingGenerator = true;
    await this.persistCurrentGeneratorInputInHistory();
    await this.navigationService.navigate('/generator', { moduleId: targetModuleId, replaceInHistory: false, reloadRoute: true });
  }

  public async onAlternativeMaterialGeneratorSelected(entry: { generator: ModuleData; typecode: string }, selectionEvent?: { isUserInput?: boolean; source?: { selected?: boolean } }) {
    this.logger.info('(generator.component) Alternative Material Generator selected: ' + entry.generator.moduleId + ' via ' + entry.typecode);
    if (selectionEvent && (!selectionEvent.isUserInput || !selectionEvent.source?.selected)) { return; }
    if (this.isSwitchingGenerator) { return; }
    const targetModuleId = (entry.generator?.moduleId || '').trim().toUpperCase();
    const normalizedInput = (this.typeCodeInputPart || '').trim().toUpperCase();
    this.isSwitchingGenerator = true;
    await this.persistCurrentGeneratorInputInHistory();
    await this.navigationService.navigate('/generator', { 
      moduleId: `BOS.${targetModuleId}${normalizedInput}**`,
      replaceInHistory: false, 
      reloadRoute: true 
    });
  }

  // Before job submission, aligns typeCodeInputPart/typeCodeComplete/materialNumber
  // with the current autocomplete list — required when the user typed a material number
  // and the list resolved to exactly one variant.
  public syncDeviceDataWithAutocompleteData() {
    if (isMaterialNumberInput(this.typeCodeInputPart)) {
      // Input: material number
      if (this.autocompTypeListCurrent?.length === 1) {
        const variant = this.autocompTypeListCurrent[0];
        this.typeCodeInputPart = this.getTypeCodeInputPart(variant.typecode);
        this.typeCodeComplete = variant.typecode.toUpperCase();
        this.typeCodeForJobServerRequest = this.typeCodeComplete.replace(/\//g, '%2F');
        this.materialNumber = variant.materialnumber || '';
        return;
      }
      this.setDeviceDataByMatNumber(this.typeCodeInputPart);
    } else {
      // Input: typecode
      this.setMaterialNumberByTypecode(this.typeCodeComplete);
    }
  }

  public setDeviceDataByMatNumber(matnumber: string) {
    if( this.autocompTypeListCurrent) {
      for (const variant of this.autocompTypeListCurrent) {
        if (matnumber.toUpperCase() === variant.materialnumber) {
          this.typeCodeInputPart = this.getTypeCodeInputPart(variant.typecode);
          this.materialNumber = variant.materialnumber;

          // reload autocomplete
          if (this.typeCodeInputPart$.observers) {
            this.typeCodeInputPart$.next(this.typeCodeInputPart);
          }
        }
      }
    }
  }

  public setMaterialNumberByTypecode(typecode: string) {
    // check input
    if(!typecode) {
      return;
    }

    if (!this.autocompleteTypeCodeMode) {
      this.materialNumber = '';
      return;
    }

    // try to pick materialnumber from session storage (generator page)
    if (typecode.toUpperCase() === sessionStorage.getItem('ecad.generator.TypeCode')) {
      const storedMaterialNumber = sessionStorage.getItem('ecad.generator.MaterialNumber');
      if(storedMaterialNumber) {
        this.materialNumber = storedMaterialNumber;
        return;
      }
    }
    // try to pick materialnumber from session storage (overview page)
    if (typecode.toUpperCase() === sessionStorage.getItem('ecad.Category.ByTypeCode.TypeCode')) {
      const storedMaterialNumber = sessionStorage.getItem('ecad.Category.ByTypeCode.MaterialNumber');
      if(storedMaterialNumber) {
        this.materialNumber = storedMaterialNumber;
        return;
      }
    }

    // try to pick materialnumber from autocompleteListCurrent (only for 1:1 relation)
    const matchingMaterialNumbers = (this.autocompTypeListCurrent || [])
      .filter((variant) => typecode.toUpperCase() === variant.typecode && !!variant.materialnumber)
      .map((variant) => variant.materialnumber);
    const uniqueMaterialNumbers = Array.from(new Set(matchingMaterialNumbers));

    if (uniqueMaterialNumbers.length === 1) {
      this.materialNumber = uniqueMaterialNumbers[0];
    } else {
      this.materialNumber = '';
    }
  }
}


// *****************************************************************************************
// Forces change event for typecode/materialnumber input validation
// *****************************************************************************************
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    void form;
    return !!(control && control.invalid);
  }
}
